<template>
	<div class="resource-planning-collapse-users-shortcut">
		<SuiButton
			v-tooltip="tooltipText"
			:aria-label="tooltipText"
			class="resource-planning-collapse-users-shortcut__button"
			variant="unstyled"
			@click="onToggle"
		>
			<span :data-testid="`icon-${iconName}`">
				<SuiIcon
					:name="iconName"
					class="resource-planning-collapse-users-shortcut__icon"
				/>
			</span>
		</SuiButton>
	</div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/composables';
import { SuiButton, SuiIcon } from '@developers/scoro-ui';
import { type ResourcePlanningGanttConfig } from '../ResourcePlanningGantt/ResourcePlanningGanttConfig';
import { type ResourcePlanningItem } from '../../types';

export interface ResourcePlanningCollapseUsersShortcutProps {
	roleGroupId: string;
	userChildren: Required<ResourcePlanningItem>[];
	isAllCollapsed: boolean;
	config: ResourcePlanningGanttConfig;
}

const {
	roleGroupId,
	userChildren,
	config,
	isAllCollapsed: initialIsAllCollapsed,
} = defineProps<ResourcePlanningCollapseUsersShortcutProps>();

const { t } = useTranslation();

const isAllCollapsed = ref(initialIsAllCollapsed);

const iconName = computed(() =>
	isAllCollapsed.value ? 'icon-expanddown-16' : 'icon-collapse-16'
);

const tooltipText = computed(() =>
	isAllCollapsed.value
		? t('resource_planning_expand_all_users')
		: t('resource_planning_collapse_all_users')
);

const onToggle = () => {
	if (isAllCollapsed.value) {
		config.gantt.batchUpdate(() => {
			config.gantt.open(roleGroupId);
			userChildren.forEach((child) => {
				config.onExpandResource?.(child);
				config.gantt.open(child.id);
			});
		});
	} else {
		config.gantt.batchUpdate(() => {
			userChildren.forEach((child) => {
				config.gantt.close(child.id);
			});
		});
	}

	isAllCollapsed.value = !isAllCollapsed.value;
};
</script>

<style lang="scss" scoped>
.resource-planning-collapse-users-shortcut {
	$parent: &;

	&__button {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		padding: 1rem;
	}

	#{$parent}__icon {
		--sui-button-icon-color: #{$neutral-60};

		margin: 0;
	}
}
</style>
